import { TrendingUp } from "lucide-react"
import { useMemo, useState } from "react"
import type { Address } from "viem"
import type { useUserPositionsEnhanced } from "../../hooks/indexer/useUserPositionsEnhanced"
import { EmptyState } from "../ui/EmptyState"
import { ErrorState } from "../ui/ErrorState"
import { PositionCardSkeleton } from "../ui/PositionCardSkeleton"
import { Skeleton } from "../ui/Skeleton"
import { EnhancedPositionCard } from "./EnhancedPositionCard"
import { PositionsHeader, type SortDirection } from "./PositionsHeader"

type StatusFilter = "active" | "closed"

interface PositionsSectionProps {
	positionsData: ReturnType<typeof useUserPositionsEnhanced>["data"]
	isLoading: boolean
	error: Error | null
	walletAddress: Address | null
	statusFilter: StatusFilter
	onStatusFilterChange: (status: StatusFilter) => void
	variant: "owner" | "public"
}

export function PositionsSection({
	positionsData,
	isLoading,
	error,
	walletAddress,
	statusFilter,
	onStatusFilterChange,
	variant,
}: PositionsSectionProps) {
	const [sortDirection, setSortDirection] = useState<SortDirection>("desc")

	const handleSort = () => {
		setSortDirection(prev => (prev === "desc" ? "asc" : "desc"))
	}

	// Check if user has any positions at all
	const totalPositions = positionsData?.positions?.length ?? 0
	const hasAnyPositions = totalPositions > 0

	// Filter positions by status and sort by value
	// Active includes both truly active AND pending (resolved but not finalized)
	const filteredPositions = useMemo(() => {
		if (!positionsData?.positions) {
			return []
		}

		const filtered = positionsData.positions.filter(p => {
			const isClosed = p.isFinalized
			// Active tab shows everything that's not finalized (active + pending)
			return statusFilter === "active" ? !isClosed : isClosed
		})

		// Sort by value
		return [...filtered].sort((a, b) => {
			let aValue: number
			let bValue: number

			if (statusFilter === "active") {
				aValue = a.currentValue
				bValue = b.currentValue
			} else {
				// For closed, sort by actual payout
				aValue = a.isResolved && a.winningBucket === a.bucketIndex ? a.shares : 0
				bValue = b.isResolved && b.winningBucket === b.bucketIndex ? b.shares : 0
			}

			return sortDirection === "desc" ? bValue - aValue : aValue - bValue
		})
	}, [positionsData?.positions, statusFilter, sortDirection])

	// Show empty state without toggle when user has no positions at all
	if (!isLoading && !error && !hasAnyPositions && variant === "owner") {
		return (
			<EmptyState
				action={{ label: "Browse Markets", href: "/" }}
				compact
				description="Take a position on where CT attention moves next week."
				icon={TrendingUp}
				title="You don't have any positions yet"
			/>
		)
	}

	// Show header during loading (skeleton) or when user has positions
	const showHeader = isLoading || hasAnyPositions

	return (
		<>
			{/* Header with filters and sort - unified component */}
			{showHeader &&
				(isLoading ? (
					<PositionsHeaderSkeleton />
				) : (
					<PositionsHeader
						onSort={handleSort}
						onStatusFilterChange={onStatusFilterChange}
						sortDirection={sortDirection}
						statusFilter={statusFilter}
					/>
				))}

			{isLoading ? (
				<div className="space-y-3 sm:space-y-4">
					{[0, 1, 2].map(i => (
						<PositionCardSkeleton key={i} />
					))}
				</div>
			) : error ? (
				<ErrorState title="Failed to load positions" variant="inline" />
			) : filteredPositions.length > 0 ? (
				<div className="space-y-3 sm:space-y-4">
					{filteredPositions.map(position =>
						walletAddress ? (
							<EnhancedPositionCard
								key={`${position.marketAddress}-${position.bucketIndex}`}
								position={position}
								userAddress={walletAddress}
								variant={variant}
							/>
						) : null,
					)}
				</div>
			) : (
				<EmptyState
					compact
					description={
						statusFilter === "active"
							? variant === "owner"
								? "Your positions will appear here once you trade."
								: "This user has no active positions."
							: variant === "owner"
								? "Your closed positions will appear here."
								: "This user has no closed positions."
					}
					icon={TrendingUp}
					title={statusFilter === "active" ? "No Active Positions" : "No Closed Positions"}
				/>
			)}
		</>
	)
}

/**
 * Skeleton for PositionsHeader - matches header layout to prevent layout shift
 * Shows filters + sort inline on mobile, filters row + column headers on desktop
 */
function PositionsHeaderSkeleton() {
	return (
		<>
			{/* Mobile: filters left, sort right */}
			<div className="mb-3 flex items-center justify-between gap-2 sm:hidden">
				<div className="flex items-center gap-1.5">
					<Skeleton className="h-8 w-[52px] rounded-lg" />
					<Skeleton className="h-8 w-[56px] rounded-lg" />
				</div>
				<Skeleton className="h-8 w-[62px] rounded-lg" />
			</div>

			{/* Desktop: filters row then header row */}
			<div className="hidden sm:block">
				<div className="mb-3 flex items-center gap-1.5">
					<Skeleton className="h-8 w-[52px] rounded-lg" />
					<Skeleton className="h-8 w-[56px] rounded-lg" />
				</div>
				<div className="mb-3 flex items-center gap-3 rounded-lg bg-bg-secondary/50 px-4 py-2">
					{/* Avatar spacer */}
					<div className="size-8 shrink-0" />
					{/* Market column */}
					<div className="min-w-0 flex-1">
						<Skeleton className="h-3 w-12" />
					</div>
					{/* Avg column */}
					<div className="w-16 shrink-0 text-right">
						<Skeleton className="ml-auto h-3 w-6" />
					</div>
					{/* Current column */}
					<div className="w-16 shrink-0 text-right">
						<Skeleton className="ml-auto h-3 w-10" />
					</div>
					{/* Value column */}
					<div className="w-28 shrink-0 text-right">
						<Skeleton className="ml-auto h-3 w-10" />
					</div>
					{/* Action spacer */}
					<div className="w-10 shrink-0" />
				</div>
			</div>
		</>
	)
}
